import type { CSSProperties } from 'react';
import {
  clientRectToLayoutRect,
  getLayoutViewportSize,
  readPageZoom,
  PAGE_ZOOM_DEFAULT,
} from './pageZoom';

/** 프로그램 표 — 평가 셀 클릭 시 드롭다운(입력없음·계획·진행·완료) 가로 */
export const PROGRAM_EVAL_DROPDOWN_WIDTH_PX = 90;

/** 준비물·단원 메모 등 셀 호버 미니 모달 표시 지연 */
export const PROGRAM_TABLE_CELL_HOVER_TOOLTIP_DELAY_MS = 1000;

/** 학습계획 호버: 셀과 팝업 밖이면 이 시간 뒤에 닫는다 */
export const LEARNING_PLAN_HOVER_TOOLTIP_DISMISS_MS = 500;

/**
 * 셀 미니 모달 clamp 영역: 테이블 스크롤 뷰포트가 아니라
 * 표가 들어 있는 섹션(카드) 전체 — 툴바까지 포함해 위·아래 잘림을 줄인다.
 *
 * 주의: `Element.closest('a, b')`는 더 가까운 쪽을 고르므로
 * 안쪽 wrap이 카드보다 먼저 잡힌다. 카드 선택자를 따로 우선한다.
 */
const PROGRAM_TABLE_FLOATING_CARD_SELECTOR = [
  'article.cp-program-progress-page-table-card',
  'article.cp-annual-progress-card',
  '.cp-program-progress-page-table-card',
  '.cp-annual-progress-card',
].join(', ');

const PROGRAM_TABLE_FLOATING_WRAP_SELECTOR = [
  '.cp-program-progress-table-wrap',
  '.cp-annual-progress-table-wrap',
  '.cp-annual-timetable-table-wrap',
].join(', ');

/** @deprecated 호환용 — resolveProgramTableFloatingHostRect가 카드 우선으로 해석 */
export const PROGRAM_TABLE_FLOATING_HOST_SELECTOR = [
  PROGRAM_TABLE_FLOATING_CARD_SELECTOR,
  PROGRAM_TABLE_FLOATING_WRAP_SELECTOR,
].join(', ');

/** `fromEl`에서 표 섹션(카드 우선) viewport rect (클라이언트/시각 좌표) */
export function resolveProgramTableFloatingHostRect(
  fromEl: Element | null | undefined
): DOMRect | null {
  if (!fromEl) return null;
  const card = fromEl.closest(PROGRAM_TABLE_FLOATING_CARD_SELECTOR);
  if (card) return card.getBoundingClientRect();
  const wrap = fromEl.closest(PROGRAM_TABLE_FLOATING_WRAP_SELECTOR);
  if (wrap) return wrap.getBoundingClientRect();
  return fromEl.getBoundingClientRect();
}

/** 단원·범교과·메모 모달과 동일 규칙의 가로 폭(호버 미리보기·앵커 재계산 공통) */
export type ProgramProgressOverlayWidthKind =
  | 'unit'
  | 'cross'
  | 'memo_narrow'
  | 'memo_wide';

export function getProgramProgressOverlayWidth(
  r: DOMRect | { width: number },
  kind: ProgramProgressOverlayWidthKind,
  pad = 8
): { width: number; maxWidth: CSSProperties['maxWidth'] } {
  const { width: vw } = getLayoutViewportSize();
  switch (kind) {
    case 'cross':
      return {
        width: Math.min(Math.max(420, r.width), 560, vw - pad * 2),
        maxWidth: 560,
      };
    case 'unit':
      return {
        width: Math.min(Math.max(r.width * 1.5, 220), vw - pad * 2),
        maxWidth: 'min(480px, calc(100vw / var(--app-page-zoom, 1) - 16px))',
      };
    case 'memo_wide':
      return {
        width: Math.min(Math.max(r.width * 1.5, 340), 580, vw - pad * 2),
        maxWidth: 'min(580px, calc(100vw / var(--app-page-zoom, 1) - 16px))',
      };
    case 'memo_narrow':
    default:
      return {
        width: Math.min(Math.max(r.width * 1.5, 220), 480, vw - pad * 2),
        maxWidth: 'min(480px, calc(100vw / var(--app-page-zoom, 1) - 16px))',
      };
  }
}

export type ProgramTableFloatingAlignX = 'left' | 'right';

/**
 * 셀 앵커 기준 미니모달 fixed 위치.
 * - `--app-page-zoom`(네비 -/+ ) 적용 시 GBR(시각) → layout 좌표로 변환해 fixed top/left에 맞춤
 * - measuredWidth(offsetWidth)는 이미 layout px — zoom으로 나누지 않음
 * - measuredWidth가 0이면 preferredWidth 사용
 * - alignX 'left': 모달 좌측 = 셀 좌측
 * - alignX 'right': 모달 우측하단 ↔ 셀 좌측상단(모달이 셀 왼쪽 위에 붙음)
 */
export function computeProgramTableFloatingPopupStyle(opts: {
  anchorRect: DOMRect;
  hostRect: DOMRect | null | undefined;
  preferredWidth: number;
  measuredWidth?: number | null;
  alignX?: ProgramTableFloatingAlignX;
  pad?: number;
  gap?: number;
  zIndex?: number;
  minReadableHeight?: number;
  maxWidthCss?: CSSProperties['maxWidth'];
  /** 내용 높이. 있으면 뷰포트 안에서 모달이 그 높이만큼 커지도록 위아래로 옮긴다. */
  contentHeight?: number;
}): CSSProperties {
  const pad = opts.pad ?? 8;
  const gap = opts.gap ?? 2;
  const minReadable = opts.minReadableHeight ?? 160;
  const alignX = opts.alignX ?? 'left';
  const { width: vw, height: vh } = getLayoutViewportSize();
  const r = clientRectToLayoutRect(opts.anchorRect);
  const host = opts.hostRect ? clientRectToLayoutRect(opts.hostRect) : null;

  const boundsLeft = Math.max(pad, Math.floor(host?.left ?? 0) + 2);
  const boundsRight = Math.min(vw - pad, Math.ceil(host?.right ?? vw) - 2);
  const boundsTop = Math.max(pad, Math.floor(host?.top ?? 0) + 2);
  const boundsBottom = Math.min(vh - pad, Math.ceil(host?.bottom ?? vh) - 2);
  const maxUsableWidth = Math.max(180, boundsRight - boundsLeft);

  const measured =
    typeof opts.measuredWidth === 'number' && opts.measuredWidth > 1
      ? opts.measuredWidth
      : null;
  const finalW = Math.min(Math.max(measured ?? opts.preferredWidth, 1), maxUsableWidth);
  const maxLeft = Math.max(boundsLeft, boundsRight - finalW);
  /** right: 모달 오른쪽 모서리 = 셀 왼쪽 모서리 */
  const desiredLeft = alignX === 'right' ? r.left - finalW : r.left;
  const left = Math.min(Math.max(boundsLeft, desiredLeft), maxLeft);

  const spaceBelow = boundsBottom - (r.bottom + gap);
  const spaceAbove = r.top - gap - boundsTop;
  const placeBelow = spaceBelow >= spaceAbove;
  const contentHeight = opts.contentHeight;

  if (contentHeight != null && contentHeight > 1) {
    const usable = Math.max(minReadable, boundsBottom - boundsTop);
    const needed = Math.min(Math.ceil(contentHeight / 4) * 4, usable);
    let top = placeBelow ? r.bottom + gap : r.top - gap - needed;
    if (top + needed > boundsBottom) top = boundsBottom - needed;
    if (top < boundsTop) top = boundsTop;
    return {
      position: 'fixed',
      top,
      bottom: 'auto',
      left,
      right: 'auto',
      width: finalW,
      maxWidth: opts.maxWidthCss ?? finalW,
      maxHeight: usable,
      overflow: contentHeight > usable - 8 ? 'auto' : 'visible',
      zIndex: opts.zIndex ?? 10060,
    };
  }

  if (placeBelow) {
    const top = Math.min(
      Math.max(boundsTop, r.bottom + gap),
      Math.max(boundsTop, boundsBottom - minReadable)
    );
    return {
      position: 'fixed',
      top,
      bottom: 'auto',
      left,
      right: 'auto',
      width: finalW,
      maxWidth: opts.maxWidthCss ?? finalW,
      maxHeight: Math.max(120, boundsBottom - top),
      zIndex: opts.zIndex ?? 10060,
    };
  }

  const bottom = Math.min(
    Math.max(vh - boundsBottom, vh - (r.top - gap)),
    Math.max(vh - boundsBottom, vh - boundsTop - minReadable)
  );
  return {
    position: 'fixed',
    top: 'auto',
    bottom,
    left,
    right: 'auto',
    width: finalW,
    maxWidth: opts.maxWidthCss ?? finalW,
    maxHeight: Math.max(120, vh - bottom - boundsTop),
    zIndex: opts.zIndex ?? 10060,
  };
}

/** 연간시간표 — 주 수업시수 셀 호버: 편제 수에 맞춘 가로 폭, 셀 왼쪽 정렬.
 * `measuredWidth`는 layout px(`offsetWidth`)를 넘긴다. GBR.width(시각)를 넣으면 배율≠100%에서 합계 여백이 깨진다.
 */
export function computeWeekTrackHoursHoverTooltipStyle(
  r: DOMRect,
  measuredWidth?: number
): CSSProperties {
  const pad = 8;
  const gap = 6;
  const { width: vw, height: vh } = getLayoutViewportSize();
  const maxWidthPx = Math.min(900, vw - pad * 2);
  const layout = clientRectToLayoutRect(r);
  const contentWidth =
    typeof measuredWidth === 'number' && measuredWidth > 1
      ? Math.min(measuredWidth, maxWidthPx)
      : undefined;

  // 셀 왼쪽에 붙이고 translate(-100%)로 왼쪽으로 펼침
  let left = layout.left - gap;
  if (contentWidth !== undefined && left - contentWidth < pad) {
    left = pad + contentWidth;
  }
  // 세로도 뷰포트 안으로
  const halfH = 28;
  let top = layout.top + layout.height / 2;
  top = Math.min(Math.max(pad + halfH, top), vh - pad - halfH);

  return {
    position: 'fixed',
    left,
    top,
    transform: 'translate(-100%, -50%)',
    width: contentWidth ?? 'max-content',
    maxWidth: maxWidthPx,
    zIndex: 10055,
    right: 'auto',
    bottom: 'auto',
    opacity: 1,
    visibility: 'visible',
    pointerEvents: 'none',
  };
}

/** 연간시간표 셀 — 라벨/전담/프로젝트 미니 모달: 셀 위 중앙, 내용 폭. */
export function computeAnnualCellColorHoverTooltipStyle(r: DOMRect): CSSProperties {
  const pad = 8;
  const gap = 6;
  const layout = clientRectToLayoutRect(r);
  const { width: vw, height: vh } = getLayoutViewportSize();
  const maxWidthPx = Math.min(280, vw - pad * 2);
  const estW = Math.min(Math.max(layout.width, 72), maxWidthPx);
  let left = layout.left + layout.width / 2;
  left = Math.min(Math.max(pad + estW / 2, left), vw - pad - estW / 2);

  const estH = 36;
  const spaceAbove = layout.top - pad;
  const spaceBelow = vh - layout.bottom - pad;
  const placeBelow = spaceAbove < estH + gap && spaceBelow > spaceAbove;

  const style: CSSProperties = {
    position: 'fixed',
    left,
    transform: 'translateX(-50%)',
    width: 'max-content',
    maxWidth: maxWidthPx,
    zIndex: 10056,
    right: 'auto',
    opacity: 1,
    visibility: 'visible',
    pointerEvents: 'none',
  };
  if (placeBelow) {
    style.top = layout.bottom + gap;
    style.bottom = 'auto';
  } else {
    style.top = 'auto';
    style.bottom = vh - layout.top + gap;
  }
  return style;
}

/** 프로그램·연간진도표: 스크롤 밖 메모 미리보기(body 포털) 위치·크기 */
export function computeProgramProgressHoverTooltipStyle(
  r: DOMRect,
  layoutKind: ProgramProgressOverlayWidthKind
): CSSProperties {
  const pad = 8;
  const gap = 6;
  const layout = clientRectToLayoutRect(r);
  const { width: vw, height: vh } = getLayoutViewportSize();
  const { width: w, maxWidth: maxW } = getProgramProgressOverlayWidth(layout, layoutKind, pad);
  /** 모달 우측 = 셀 좌측 (셀 왼쪽 위에 표시) */
  let left = layout.left - w;
  left = Math.min(Math.max(pad, left), vw - w - pad);

  const estH = 100;
  const spaceAbove = layout.top - pad;
  const spaceBelow = vh - layout.bottom - pad;
  const placeBelow = spaceAbove < estH && spaceBelow > spaceAbove;

  const style: CSSProperties = {
    position: 'fixed',
    left,
    width: w,
    maxWidth: maxW,
    zIndex: 10055,
    transform: 'none',
    right: 'auto',
    opacity: 1,
    visibility: 'visible',
    pointerEvents: 'none',
  };
  if (placeBelow) {
    style.top = layout.bottom + gap;
    style.bottom = 'auto';
  } else {
    style.top = 'auto';
    style.bottom = vh - layout.top + gap;
  }
  return style;
}

/**
 * 학습계획 호버: 학습내용 셀 왼쪽에 두고, 팝업 오른쪽이 셀 왼쪽에 붙는다.
 * 세로는 화면 높이의 60%를 넘지 않고, 마우스를 올려 스크롤할 수 있다.
 */
export function computeLearningPlanHoverTooltipStyle(r: DOMRect): CSSProperties {
  const pad = 8;
  const layout = clientRectToLayoutRect(r);
  const { width: vw, height: vh } = getLayoutViewportSize();
  const room = Math.max(0, layout.left - pad);
  const width = room > 0 ? Math.min(320, room) : Math.min(220, vw - pad * 2);
  const maxH = Math.max(160, vh * 0.6);
  let top = pad;
  if (layout.top > top + maxH - 24) {
    top = Math.min(layout.top, Math.max(pad, vh - pad - maxH));
  }
  return {
    position: 'fixed',
    top,
    left: 'auto',
    right: Math.max(0, vw - layout.left),
    bottom: 'auto',
    width,
    maxWidth: width,
    maxHeight: maxH,
    zIndex: 10055,
    transform: 'none',
    overflowX: 'hidden',
    overflowY: 'auto',
    opacity: 1,
    visibility: 'visible',
    pointerEvents: 'auto',
  };
}

/** 주/학기 시수 호버 미리보기 자연 폭(layout px). style.width가 잘못 잠겨 있어도 그리드 기준으로 측정. */
export function measureWeekTrackHoursPreviewLayoutWidth(preview: HTMLElement): number {
  const grid = preview.querySelector('.cp-annual-week-hours-hover-grid');
  if (grid instanceof HTMLElement && grid.offsetWidth > 1) {
    const cs = window.getComputedStyle(preview);
    const padX =
      (Number.parseFloat(cs.paddingLeft) || 0) + (Number.parseFloat(cs.paddingRight) || 0);
    const borderX =
      (Number.parseFloat(cs.borderLeftWidth) || 0) + (Number.parseFloat(cs.borderRightWidth) || 0);
    return Math.ceil(grid.offsetWidth + padX + borderX);
  }
  const prevInlineWidth = preview.style.width;
  preview.style.width = 'max-content';
  const w = preview.offsetWidth;
  preview.style.width = prevInlineWidth;
  return w;
}

/** preferredWidth 계산 시 셀 폭도 layout 기준으로 */
export function layoutWidthFromClientRect(r: DOMRect): number {
  const zoom = readPageZoom() || PAGE_ZOOM_DEFAULT;
  if (Math.abs(zoom - 1) < 0.001) return r.width;
  return r.width / zoom;
}
